import React, { useState, useEffect } from 'react';
import { authUtils } from '../utils/auth';
import './UserManagement.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [brands, setBrands] = useState([]);
  const [spvs, setSPVs] = useState([]);
  const [filters, setFilters] = useState({
    role: '',
    brand: '',
    search: ''
  });

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    full_name: '',
    role: 'operator',
    brand: '',
    spv_id: ''
  });

  const currentUser = authUtils.getUser();

  useEffect(() => {
    fetchUsers();
    fetchBrands();
    fetchSPVs();
  }, [filters]);

  const fetchUsers = async () => {
    try {
      const queryParams = new URLSearchParams();
      if (filters.role) queryParams.append('role', filters.role);
      if (filters.brand) queryParams.append('brand', filters.brand);
      if (filters.search) queryParams.append('search', filters.search);

      const response = await fetch(`${API_URL}/users?${queryParams}`, {
        headers: { 'Authorization': `Bearer ${authUtils.getToken()}` }
      });

      const data = await response.json();
      if (data.success) {
        setUsers(data.users);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await fetch(`${API_URL}/users/meta/brands`, {
        headers: { 'Authorization': `Bearer ${authUtils.getToken()}` }
      });
      const data = await response.json();
      if (data.success) {
        setBrands(data.brands);
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const fetchSPVs = async () => {
    if (!authUtils.isSuperAdmin()) return;
    
    try {
      const response = await fetch(`${API_URL}/users/meta/spvs`, {
        headers: { 'Authorization': `Bearer ${authUtils.getToken()}` }
      });
      const data = await response.json();
      if (data.success) {
        setSPVs(data.spvs);
      }
    } catch (error) {
      console.error('Error fetching SPVs:', error);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API_URL}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authUtils.getToken()}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      
      if (data.success) {
        alert('User created successfully!');
        setShowModal(false);
        resetForm();
        fetchUsers();
      } else {
        alert(data.error || 'Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Failed to create user');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    
    try {
      const updateData = { ...formData };
      if (!updateData.password) {
        delete updateData.password;
      }

      const response = await fetch(`${API_URL}/users/${editUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authUtils.getToken()}`
        },
        body: JSON.stringify(updateData)
      });

      const data = await response.json();
      
      if (data.success) {
        alert('User updated successfully!');
        setShowModal(false);
        setEditUser(null);
        resetForm();
        fetchUsers();
      } else {
        alert(data.error || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const response = await fetch(`${API_URL}/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authUtils.getToken()}` }
      });

      const data = await response.json();
      
      if (data.success) {
        alert('User deleted successfully!');
        fetchUsers();
      } else {
        alert(data.error || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user');
    }
  };

  const openCreateModal = () => {
    setEditUser(null);
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setEditUser(user);
    setFormData({
      username: user.username,
      password: '',
      full_name: user.full_name || '',
      role: user.role,
      brand: user.brand || '',
      spv_id: user.spv_id || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      full_name: '',
      role: 'operator',
      brand: '',
      spv_id: ''
    });
  };

  const getRoleBadge = (role) => {
    const badges = {
      super_admin: '👑 Super Admin',
      spv: '👨‍💼 SPV',
      operator: '👤 Operator'
    };
    return badges[role] || role;
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="user-management">
      <div className="header">
        <h1>User Management</h1>
        <button className="btn-primary" onClick={openCreateModal}>
          ➕ Create User
        </button>
      </div>

      {/* Filters */}
      <div className="filters">
        <select 
          value={filters.role} 
          onChange={(e) => setFilters({...filters, role: e.target.value})}
        >
          <option value="">All Roles</option>
          <option value="spv">SPV</option>
          <option value="operator">Operator</option>
        </select>

        <select 
          value={filters.brand} 
          onChange={(e) => setFilters({...filters, brand: e.target.value})}
        >
          <option value="">All Brands</option>
          {brands.map(brand => (
            <option key={brand} value={brand}>{brand}</option>
          ))}
        </select>

        <input
          type="text"
          placeholder="Search username or name..."
          value={filters.search}
          onChange={(e) => setFilters({...filters, search: e.target.value})}
        />
      </div>

      {/* Users Table */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Username</th>
              <th>Full Name</th>
              <th>Role</th>
              <th>Brand</th>
              <th>SPV</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id}>
                <td>{user.username}</td>
                <td>{user.full_name || '-'}</td>
                <td>
                  <span className={`badge badge-${user.role}`}>
                    {getRoleBadge(user.role)}
                  </span>
                </td>
                <td>{user.brand || '-'}</td>
                <td>{user.spv_name || '-'}</td>
                <td>
                  <span className={`status ${user.is_active ? 'active' : 'inactive'}`}>
                    {user.is_active ? '✓ Active' : '✗ Inactive'}
                  </span>
                </td>
                <td>
                  <button 
                    className="btn-small btn-edit" 
                    onClick={() => openEditModal(user)}
                  >
                    Edit
                  </button>
                  {['super_admin', 'spv'].includes(currentUser.role) && user.id !== currentUser.id && (
				  <button
					className="btn-small btn-delete"
					onClick={() => handleDeleteUser(user.id)}
				  >
					Delete
				  </button>
				)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editUser ? 'Edit User' : 'Create New User'}</h2>
            <form onSubmit={editUser ? handleUpdateUser : handleCreateUser}>
              <div className="form-group">
                <label>Username *</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  required
                  disabled={!!editUser}
                />
              </div>

              <div className="form-group">
                <label>Password {editUser && '(leave empty to keep current)'}</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required={!editUser}
                />
              </div>

              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                />
              </div>

              {authUtils.isSuperAdmin() && (
                <>
                  <div className="form-group">
                    <label>Role *</label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({...formData, role: e.target.value})}
                      required
                    >
                      <option value="operator">Operator</option>
                      <option value="spv">SPV</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Brand</label>
                    <input
                      type="text"
                      value={formData.brand}
                      onChange={(e) => setFormData({...formData, brand: e.target.value})}
                      placeholder="e.g., Afterwin"
                    />
                  </div>

                  {formData.role === 'operator' && (
                    <div className="form-group">
                      <label>Assign to SPV</label>
                      <select
                        value={formData.spv_id}
                        onChange={(e) => setFormData({...formData, spv_id: e.target.value})}
                      >
                        <option value="">Select SPV</option>
                        {spvs.map(spv => (
                          <option key={spv.id} value={spv.id}>
                            {spv.full_name || spv.username} ({spv.brand})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}

              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editUser ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;